/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import axios from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";

export function useReservationHandlers({
  formData,
  setFormData,
  setAvailableAdults,
  setAvailableChildren,
  setRoomCategories,
  setGuestCustomers,
  setGuestChildren,
  setNightlyPrice,
  isResettingRef,
  businessUnitId,
  selectedReservationId,
  setRoomCategoryMap,
  setRoomIdMap,
  refetchReservationAfterReset,
  reloadDatesFromDB,
  setDbDatesMap,
  reloadRoomCategoryIdFromDB,
  setRoomCategoryDetailsMap,
  reloadRoomCategoryDetailsFromDB,
  reloadAdultsFromDB,
  setAdultsMap,
  reloadChildrenFromDB,
  setChildrenMap,
  setCustomerData,
  reloadRoomIdFromDB,
  reloadRoomRateIdFromDB,
  setRoomRateDetailsMap,
  reservationIdRef,
  groupReservations
}) {
const navigate = useNavigate();

// 🔒 Calcular la fecha mínima permitida para check-in (ayer)
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const minCheckInDate = yesterday.toISOString().split("T")[0];


  const handleChange = (e) => {
    if (e.target.name === "check_in_date") {
      console.log("🖐️ Usuario eligió check_in_date =", e.target.value);
    }

    const { name, value } = e.target;
    let updatedData = { ...formData, [name]: value };

    // 🛡️ Proteger de reset innecesario si valor no ha cambiado
    if (name === "check_in_date" && value === formData.check_in_date) {
      console.log("⛔ check_in_date no cambió, se ignora reset");
      return;
    }

    if (name === "check_out_date" && value === formData.check_out_date) {
      console.log("⛔ check_out_date no cambió, se ignora reset");
      return;
    }

  
    const isDateChange = name === "check_in_date" || name === "check_out_date";

    if (name === "check_in_date" && value === formData.check_in_date) {
      console.log("⚠️ check_in_date no ha cambiado. Se omite reset.");
      return; // 🔒 EVITA el reset completo si no hay cambio real
    }
  
    if (name === "check_in_date") {
      updatedData.check_out_date = "";
      updatedData.room_nights = "";
    
      // ⚠️ Lanzamos limpieza completa porque la fecha base ha cambiado
      const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
      const reservationId = pathMatch ? pathMatch[1] : null;
    
      if (reservationId && formData.status === "Draft") {
        console.log("📣 Borrando reservation-guests del backend...");
        isResettingRef.current = true;
    
        axios
          .delete(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests/by-draft/${reservationId}`)

          .then(() => {
            console.log("✅ Guests backend eliminados");
    
            const patchPayload = {
              check_in_date: value,
              check_out_date: null,
              room_category_id: null,
              room_id: null,
              rate_id: null,
              adults: null,
              children: 0,
              children_ages: null,
              total_price: null,
              room_nights: null,
              customer_id: null,
              updated_by: "AutoReset"
            };
            
    
            axios
              .patch(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}/draft`, patchPayload)
              .then(() => {
                console.log("✅ Campos limpiados en backend");

                window.__suppressAutoSave = true;
    
                // ⚠️ navigate dispara un re-render, pero actualizamos visualmente el campo también
                navigate(`/new-reservation/${reservationId}?business_unit_id=${businessUnitId}`, { replace: true });

                setTimeout(() => {
                  window.__suppressAutoSave = false;
                }, 1000);
          
                   
              })
              .catch((err) => {
                console.error("❌ Error limpiando campos en backend:", err.response?.data || err.message);
                isResettingRef.current = false;
              });
          })
          .catch((err) =>
            console.error("❌ Error al borrar guests del backend:", err.response?.data || err.message)
          );
      }
    }

    if (name === "check_out_date") {
      updatedData.room_nights = "";
    
      const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
      const reservationId = pathMatch ? pathMatch[1] : null;
    
      if (reservationId && formData.status === "Draft") {
        console.log("📣 Borrando reservation-guests del backend (check_out_date)...");
        window.__suppressAutoSave = true;
    
        console.log("🧪 [ResetDates][check_out_date] deleting draft guests", {
          reservation_draft_id: reservationId
        });

        axios
          .delete(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests/by-draft/${reservationId}`)

          .then(() => {
            console.log("✅ Guests backend eliminados (check_out_date)");
    
            const patchPayload = {
              check_out_date: value === "" ? null : value, // 🛠️ Aquí está el fix
              check_in_date: formData.check_in_date || null,
              room_category_id: null,
              room_id: null,
              rate_id: null,
              adults: null,
              children: 0,
              children_ages: null,
              total_price: null,
              room_nights: null,
              customer_id: null,
              updated_by: "AutoReset"
            };
    
            axios
              .patch(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}/draft`, patchPayload)
              .then(() => {
                console.log("✅ Campos limpiados en backend (check_out_date)");
    
                navigate(`/new-reservation/${reservationId}?business_unit_id=${businessUnitId}`, { replace: true });
    
                setTimeout(() => {
                  window.__suppressAutoSave = false;
                }, 1000);
              })
              .catch((err) => {
                console.error("❌ Error limpiando campos en backend (check_out_date):", err.response?.data || err.message);
                window.__suppressAutoSave = false;
              });
          })
          .catch((err) =>
            console.error("❌ Error al borrar guests del backend (check_out_date):", err.response?.data || err.message)
          );
      }
    }
    
    
    
  
    if (isDateChange) {
      setAvailableAdults([]);
      setAvailableChildren([]);
      setRoomCategories([]);
      setGuestCustomers([]);
      setGuestChildren([]);
      setNightlyPrice(null);
    }
    
    
  
    setFormData(updatedData);
  };

  const handleResetDates = async () => {
    console.log("🟡 handleResetDates: INICIADO");
    const reservationId = selectedReservationId;
    window.__preventCleanupReservationId = reservationId;
    window.__internalNavigation = true;
  
    setTimeout(() => {
      window.__internalNavigation = false;
      delete window.__preventCleanupReservationId;
    }, 2000);
  
  
    
  
    if (!reservationId || isNaN(Number(reservationId))) {
      alert("❌ No reservation ID found.");
      return;
    }
  
    try {
      console.log("♻️ Enviando petición de reset al backend...");
  
      const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
        params: { business_unit_id: businessUnitId }
      });
  
      const defaultCheckIn = settingsRes.data.checkin_time || "14:00:00";
      const defaultCheckOut = settingsRes.data.checkout_time || "10:00:00";
  
      console.log("📡 Enviando PATCH reset para reserva:", reservationId);
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}/reset`, {
        default_check_in: defaultCheckIn,
        default_check_out: defaultCheckOut
      });
  
      console.log("✅ Reset completado en backend");
  
      // 🧼 Limpieza inmediata del formulario para evitar valores residuales
      setFormData(prev => ({
        ...prev,
        room_id: "",
        rate_id: "",
        group_rate_id: null,
        total_price: "",
        night_price: ""
      }));
  
      // 🧼 Limpiar nombre de Room Category del cuadro informativo
      setRoomCategoryMap(prev => ({
        ...prev,
        [reservationId]: null
      }));
      setRoomIdMap(prev => ({
        ...prev,
        [reservationId]: null
      }));
  
      
  
  
  
      // 🔁 Recargar la reserva desde la base de datos
      await refetchReservationAfterReset({
        reservationId,
        setFormData,
        setCustomerData,
        setGuestCustomers,
        setGuestChildren,
        formData
      });
      await reloadDatesFromDB(reservationId, setDbDatesMap);
      await reloadRoomCategoryIdFromDB(reservationId, setRoomCategoryMap);
      await reloadRoomCategoryDetailsFromDB(reservationId, setRoomCategoryDetailsMap);
      await reloadAdultsFromDB(reservationId, setAdultsMap);
      await reloadChildrenFromDB(reservationId, setChildrenMap);

  
  
  
    } catch (error) {
      console.error("❌ ERROR en reset PATCH:", error.response?.data || error.message);
      alert("Error resetting reservation.");
    }
  
  };

  const handleResetCapacityOnly = async () => {
  console.log("🟡 handleResetCapacityOnly: INICIADO");
  const reservationId = selectedReservationId;
  window.__preventCleanupReservationId = reservationId;
  window.__internalNavigation = true;

  setTimeout(() => {
    window.__internalNavigation = false;
    delete window.__preventCleanupReservationId;
  }, 2000);

  if (!reservationId || isNaN(Number(reservationId))) {
    alert("❌ No reservation ID found.");
    return;
  }

  try {
    console.log("♻️ Enviando petición de reset de capacidad al backend...");

    // ⚙️ Obtener settings para mantener los check-in/out time por defecto
    const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
      params: { business_unit_id: businessUnitId }
    });

    const defaultCheckIn = settingsRes.data.checkin_time || "14:00:00";
    const defaultCheckOut = settingsRes.data.checkout_time || "10:00:00";

    // 🧹 Enviar PATCH para resetear todo salvo fechas
    await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}/reset-capacity`, {
      default_check_in: defaultCheckIn,
      default_check_out: defaultCheckOut,
      updated_by: "AutoResetCapacity"
    });

    console.log("✅ Reset de capacidad completado en backend");

    // 🧼 Limpieza local de campos relacionados a capacidad
    setFormData(prev => ({
      ...prev,
      room_id: "",
      rate_id: "",
      group_rate_id: null,
      room_category_id: "",
      total_price: "",
      night_price: "",
      adults: null,
      children: 0,
      children_ages: "",
      customer_id: null
    }));

    setRoomCategoryMap(prev => ({ ...prev, [reservationId]: null }));
    setRoomIdMap(prev => ({ ...prev, [reservationId]: null }));

    // 🔁 Recargar todos los datos relacionados (menos fechas)
    await refetchReservationAfterReset({
      reservationId: selectedReservationId,
      setFormData,
      setCustomerData,
      setGuestCustomers,
      setGuestChildren,
      formData // puedes ponerlo o quitarlo según lo uses en el util
    });
    await reloadRoomCategoryIdFromDB(reservationId, setRoomCategoryMap);
    await reloadRoomCategoryDetailsFromDB(reservationId, setRoomCategoryDetailsMap);
    await reloadRoomIdFromDB(reservationId, setRoomIdMap);
    await reloadRoomRateIdFromDB(reservationId, setRoomRateDetailsMap);
    await reloadAdultsFromDB(reservationId, setAdultsMap);
    await reloadChildrenFromDB(reservationId, setChildrenMap);

  } catch (error) {
    console.error("❌ ERROR en reset-capacity:", error.response?.data || error.message);
    alert("Error resetting capacity.");
  }
};

const handleResetChildrenCapacityOnly = async () => {
  console.log("🟡 handleResetChildrenCapacityOnly: INICIADO");
  const reservationId = selectedReservationId;
  const previousAdults = formData.adults; // 🟩 guardamos adultos actuales

  window.__preventCleanupReservationId = reservationId;
  window.__internalNavigation = true;

  setTimeout(() => {
    window.__internalNavigation = false;
    delete window.__preventCleanupReservationId;
  }, 2000);

  if (!reservationId || isNaN(Number(reservationId))) {
    alert("❌ No reservation ID found.");
    return;
  }

  try {
    console.log("♻️ Enviando petición de reset de capacidad para children al backend...");

    // ⚙️ Obtener settings para mantener los check-in/out time por defecto
    const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
      params: { business_unit_id: businessUnitId }
    });

    const defaultCheckIn = settingsRes.data.checkin_time || "14:00:00";
    const defaultCheckOut = settingsRes.data.checkout_time || "10:00:00";

    // 🧹 Enviar PATCH para resetear solo lo relacionado con children
    await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}/reset-children-capacity`, {
      default_check_in: defaultCheckIn,
      default_check_out: defaultCheckOut,
      updated_by: "AutoResetChildrenCapacity"
    });

    console.log("✅ Reset de capacidad para children completado en backend");

    // 🧼 Limpieza local solo para children y dependientes (NO tocar adults)
    setFormData(prev => ({
      ...prev,
      children: 0,
      children_ages: "",
      room_category_id: "",
      room_id: "",
      rate_id: "",
      group_rate_id: null,
      total_price: "",
      night_price: ""
      // no tocamos adults aquí
    }));

    setChildrenMap(prev => ({ ...prev, [reservationId]: 0 }));
    setRoomCategoryMap(prev => ({ ...prev, [reservationId]: null }));
    setRoomIdMap(prev => ({ ...prev, [reservationId]: null }));
    setRoomRateDetailsMap(prev => ({ ...prev, [reservationId]: null }));

    // 🔁 Recarga selectiva relacionada con children
    await refetchReservationAfterReset({
      reservationId,
      setFormData,
      setCustomerData,
      setGuestCustomers,
      setGuestChildren,
      formData
    });
    await reloadRoomCategoryIdFromDB(reservationId, setRoomCategoryMap);
    await reloadRoomCategoryDetailsFromDB(reservationId, setRoomCategoryDetailsMap);
    await reloadRoomIdFromDB(reservationId, setRoomIdMap);
    await reloadRoomRateIdFromDB(reservationId, setRoomRateDetailsMap);
    await reloadChildrenFromDB(reservationId, setChildrenMap);

    // 🟩 Restaurar adults si el backend los ha puesto a null
    if (previousAdults != null) {
      console.log("[ChildrenReset] Restoring adults after reset:", previousAdults);
      setFormData(prev => ({
        ...prev,
        adults: previousAdults
      }));
      setAdultsMap(prev => ({
        ...prev,
        [reservationId]: previousAdults
      }));
    }

  } catch (error) {
    console.error("❌ ERROR en reset-children-capacity:", error.response?.data || error.message);
    alert("Error resetting children capacity.");
  }
};


const handleResetRoomCategoryOnly = async () => {
  console.log("🟡 handleResetRoomCategoryOnly: INICIADO");
  const reservationId = selectedReservationId;
  window.__preventCleanupReservationId = reservationId;
  window.__internalNavigation = true;

  setTimeout(() => {
    window.__internalNavigation = false;
    delete window.__preventCleanupReservationId;
  }, 2000);

  if (!reservationId || isNaN(Number(reservationId))) {
    alert("❌ No reservation ID found.");
    return;
  }

  try {
    console.log("♻️ Enviando petición de reset de room category al backend...");

    // Obtener settings para mantener tiempos check-in/out
    const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
      params: { business_unit_id: businessUnitId }
    });

    const defaultCheckIn = settingsRes.data.checkin_time || "14:00:00";
    const defaultCheckOut = settingsRes.data.checkout_time || "10:00:00";

    // PATCH para reset room_category y dependientes (sin tocar adults ni children)
    await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}/reset-room-category-only`, {
      default_check_in: defaultCheckIn,
      default_check_out: defaultCheckOut,
      updated_by: "AutoResetRoomCategoryOnly"
    });

    console.log("✅ Reset de room category completado en backend");

    // Limpieza local solo de room category y dependientes, sin tocar adults ni children
    setFormData(prev => {
      const newState = {
        ...prev,
        room_category_id: "",
        room_id: "",
        rate_id: "",
        group_rate_id: null,
        total_price: "",
        night_price: ""
        // NO tocar adults ni children aquí
      };
      console.log("🔎 [TEST] formData tras reset de categoría:", newState);
      return newState;
    });


    setRoomCategoryMap(prev => ({ ...prev, [reservationId]: null }));
    setRoomIdMap(prev => ({ ...prev, [reservationId]: null }));
    setRoomRateDetailsMap(prev => ({ ...prev, [reservationId]: null }));

    // Recarga selectiva relacionada con room category
    await refetchReservationAfterReset({
      reservationId,
      setFormData,
      setCustomerData,
      setGuestCustomers,
      setGuestChildren,
      formData
    });
    await reloadRoomCategoryIdFromDB(reservationId, setRoomCategoryMap);
    await reloadRoomCategoryDetailsFromDB(reservationId, setRoomCategoryDetailsMap);
    await reloadRoomIdFromDB(reservationId, setRoomIdMap);
    await reloadRoomRateIdFromDB(reservationId, setRoomRateDetailsMap);

  } catch (error) {
    console.error("❌ ERROR en reset-room-category-only:", error.response?.data || error.message);
    alert("Error resetting room category.");
  }
};



const handleDeleteReservation = async () => {
    const currentReservationId = reservationIdRef.current;
  
    if (!currentReservationId) {
      alert("Reservation ID missing.");
      return;
    }
  
    const message = groupReservations.length > 1
      ? "Are you sure you want to delete these reservations? This action cannot be undone."
      : "Are you sure you want to delete this reservation? This action cannot be undone.";

    const confirmed = window.confirm(message);

    if (!confirmed) return;
  
    try {
      // 🗑️ 1. Borrar draft-guests (source of truth en drafts)
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests/by-draft/${currentReservationId}`
        );
        console.log("🗑️ Draft guests eliminados correctamente (o no había ninguno).");
      } catch (guestError) {
        console.warn("⚠️ No se pudieron borrar draft guests:", guestError.message);
      }

  
      // 🗑️ 2. Borrar la reserva Draft
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/reservations/${currentReservationId}`);
      console.log("🗑️ Reserva borrada correctamente.");
    
      // 🚀 3. Redirigir a la lista de reservas
      navigate(`/reservations?business_unit_id=${businessUnitId}`);
    } catch (error) {
      console.error("❌ Error deleting reservation:", error);
      alert("Error deleting reservation.");
    }
  };
  
  
return {
    minCheckInDate,
    handleChange,
    handleResetDates,
    handleResetCapacityOnly,
    handleDeleteReservation,
    handleResetChildrenCapacityOnly,
    handleResetRoomCategoryOnly
  };
}